import { Order, TableData, TableError } from "@/dto/tableData";
import sendItemsToKitchen from "@/dataLayer/sendItemsToKitchen";
import { CheckoutItemModifier } from "@/dto/checkoutInfo";
import { Modifier } from "@/dto/modifier";
import { RestaurantCartItem, RestaurantCartValue } from "@/models/cart/types";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { OrderType, orderTypes, CartItemType } from "@/constants/enum";
import { TFunction } from "@/types";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { getFromLocalStorage, saveToLocalStorage } from "./localStorageUtils";
import { Dispatch, SetStateAction } from "react";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { Merchant } from "@/dto/merchant";
import { LocalStorageKey } from "./storageKeys";
import getMenu from "@/dataLayer/getMenu";
import { clientLogger } from "@/utils/clientLogger";

export const convertToScanToOrderInfo = (
  orderType: string | string[] | undefined,
  tableId: string | string[] | undefined,
  employeeId?: string | string[] | undefined
): ScanToOrderInfo | null => {
  let scanToOrderInfo = null;
  const normalizedOrderType = Array.isArray(orderType)
    ? orderType[0]
    : orderType;
  const normalizedTableId = Array.isArray(tableId) ? tableId[0] : tableId;
  const normalizedEmployeeId = Array.isArray(employeeId)
    ? employeeId[0]
    : employeeId;

  if (
    normalizedOrderType &&
    typeof normalizedOrderType === "string" &&
    isValidOrderType(normalizedOrderType.toLowerCase()) &&
    normalizedTableId &&
    typeof normalizedTableId === "string" &&
    (!normalizedEmployeeId || typeof normalizedEmployeeId === "string")
  ) {
    scanToOrderInfo = {
      orderType: normalizedOrderType.toLowerCase() as OrderType,
      tableId: normalizedTableId,
      ...(normalizedEmployeeId && { employeeId: normalizedEmployeeId }),
    };
  }
  return scanToOrderInfo;
};

export const isValidOrderType = (value: string): value is OrderType => {
  return orderTypes.some((type) => type.toLowerCase() === value);
};

export const getCartButtonText = (
  cartCount: number,
  isRunningTabsEnabled: boolean,
  t: TFunction
): string => {
  let text = "";
  if (isRunningTabsEnabled) {
    switch (cartCount) {
      case 0:
        text = t("closeAndPay");
        break;
      case 1:
        text = t("sendItemToKitchen");
        break;
      default:
        text = t("sendItemsToKitchen", { cartCount });
        break;
    }
    return text;
  } else {
    return t("continueToCheckout");
  }
};

export const sendToKitchen = async (
  merchant: Merchant,
  recaptcha: string,
  cartItems: RestaurantCartItem[],
  specialInstructions: string,
  tableId: string,
  merchantId: string,
  tableData: TableData,
  onRequestClose: () => void,
  setIsSentToKitchen: Dispatch<SetStateAction<boolean>>,
  setFailedToSendToKitchen: Dispatch<SetStateAction<boolean>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  employeeId?: string,
  menuId?: string
) => {
  const items = cartItems.flatMap((item: RestaurantCartItem) => {
    const {
      id,
      modifiers,
      isAgeRestricted,
      specialInstructions,
      quantity,
      menuId,
    } = item.value;
    const checkoutItemModifiers: CheckoutItemModifier[] = modifiers
      .filter((modifier) => modifier.id !== undefined)
      .flatMap((modifier: Modifier) =>
        Array.from({ length: modifier.quantity ?? 1 }, () => ({
          id: modifier.id!,
        }))
      );
    const cartItem = {
      id,
      itemModifiers: checkoutItemModifiers,
      isAgeRestricted: isAgeRestricted ?? false,
      note: specialInstructions,
      ...(menuId && { menuId }),
    };
    return Array.from({ length: quantity || 1 }, () => cartItem);
  });
  const payload = {
    cart: { giftCards: [], items: items },
    note: specialInstructions,
    typeMetadata: {
      tableUuid: tableId,
      employeeUuid: employeeId ?? "",
    },
  };
  try {
    const data = await sendItemsToKitchen(
      merchantId,
      tableId,
      recaptcha,
      payload,
      menuId
    );

    if (data) {
      // Preserve the original cart insertion order in the API response.
      // The server may reorder items (e.g. by ID or grouping logic), so we
      // re-sort data.cart.items to match the order the user added them.
      const cartItemOrder = cartItems.map((item) => item.value.id);
      const sortedItems = [...(data.cart?.items ?? [])].sort((a, b) => {
        const aIndex = cartItemOrder.indexOf(a.id);
        const bIndex = cartItemOrder.indexOf(b.id);
        // Items not present in the original cart (e.g. previously sent rounds)
        // fall back to their existing relative position.
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
      const orderedData = {
        ...data,
        cart: { ...data.cart, items: sortedItems },
      };
      const newTableData = { ...tableData, order: orderedData };
      RestaurantCart.clear();
      saveToLocalStorage(LocalStorageKey.TABLE_DATA, newTableData);
      setIsLoading(false);
      onRequestClose();
      setIsSentToKitchen(true);
    }
  } catch (error) {
    clientLogger.error(`${merchant.slug} - Error in sendToKitchen: `, error);
    setFailedToSendToKitchen(true);
    setIsLoading(false);
  }
};

export const goToGuestCheckout = (
  router: Pick<AppRouterInstance, "push">,
  scanToOrderInfo?: ScanToOrderInfo
) => {
  let queryParams = "";
  if (scanToOrderInfo) {
    queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  router.push("/guest-checkout" + queryParams);
};

export const formatOrderTypeToString = (input: string): string => {
  if (!input) {
    return "";
  }

  const firstCharUpper = input.charAt(0).toUpperCase();
  const restOfString = input.slice(1);
  const stringWithHyphens = restOfString.replace(/_/g, "-");

  return `${firstCharUpper}${stringWithHyphens}`;
};

export function buildScanToOrderSearchParams({
  orderType,
  tableId,
  employeeId,
}: Partial<ScanToOrderInfo>): string {
  const params = new URLSearchParams();
  if (orderType) {
    params.append("order_type", String(orderType));
  }
  if (tableId) {
    params.append("table_id", String(tableId));
  }
  if (employeeId) {
    params.append("employee_id", String(employeeId));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

export const convertTableDataToRestaurantCartItems = (
  tableData: TableData
): RestaurantCartItem[] => {
  if (
    !tableData ||
    !tableData.order ||
    !tableData.order.cart ||
    !tableData.order.cart.items
  ) {
    return [];
  }

  const restraurantCartItems: RestaurantCartItem[] =
    tableData.order.cart.items.map((item: RestaurantCartValue) => {
      return {
        key: item.id,
        value: {
          ...item,
          type: CartItemType.RESTAURANT,
        } as RestaurantCartValue,
      };
    });

  return restraurantCartItems;
};

export const processScanToOrderCartItems = async (
  order: Order,
  merchantId: string,
  menuId?: string
) => {
  // search for item name using item.id in menu and add to item
  const menu = await getMenu(merchantId, OrderType.PICKUP, menuId);
  const menuItems = Object.fromEntries(
    menu.items.map((item) => [item.id, item])
  );

  // rename note to special instructions
  const updatedItems = order?.cart?.items.map((item) => {
    const itemWithName = {
      ...item,
      name: item.name ?? menuItems[item.id]?.name,
    };
    if (itemWithName.note === undefined) return itemWithName;
    const { note, ...rest } = itemWithName;
    return { ...rest, specialInstructions: note };
  });

  return {
    ...order,
    cart: {
      ...order.cart,
      items: updatedItems,
    },
  };
};

export const extractScanToOrderParams = (searchParams: {
  [key: string]: string | string[] | undefined;
}) => {
  const urlSearchParams = new URLSearchParams();
  for (const key in searchParams) {
    const value = searchParams[key];
    if (Array.isArray(value)) {
      value.forEach((v) => urlSearchParams.append(key, v));
    } else if (value !== undefined) {
      urlSearchParams.append(key, value);
    }
  }

  const menuName = urlSearchParams.get("menu") ?? undefined;
  const orderType = urlSearchParams.get("order_type") ?? undefined;
  const tableId = urlSearchParams.get("table_id") ?? undefined;
  const employeeId = urlSearchParams.get("employee_id") ?? undefined;

  return {
    urlSearchParams,
    menuName,
    orderType,
    tableId,
    employeeId,
  };
};

export const getInvalidTableState = (contactServerText: string): TableData => {
  return {
    id: "",
    isValid: false,
    name: contactServerText,
    section: {
      id: "",
      name: "",
    },
    order: {
      id: "",
      additionalCharges: [],
      cart: {
        id: "",
        discounts: [],
        items: [],
      },
      metadata: {
        scheduled: false,
        scheduledPickupTime: 0,
      },
      note: "",
      paymentStatus: "",
      timePlaced: 0,
      total: 0,
      type: "",
    },
  };
};

/**
 * Preserves the insertion order of cart items based on existing local storage data.
 * New items are appended to the end of the list.
 */
export const preserveItemOrder = (
  existingItems: RestaurantCartValue[],
  responseItems: RestaurantCartValue[]
): RestaurantCartValue[] => {
  if (existingItems.length === 0 || responseItems.length === 0) {
    return responseItems;
  }

  const existingOrder = existingItems.map((item) => item.id);

  return [...responseItems].sort((a, b) => {
    const aIndex = existingOrder.indexOf(a.id);
    const bIndex = existingOrder.indexOf(b.id);

    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

const handleS2OTableAPISuccess = (
  validData: TableData,
  scanToOrderInfo?: ScanToOrderInfo | null
) => {
  // Preserve the item order already stored in TABLE_DATA.
  // sendToKitchen saves items in the user's original insertion order.
  // The polling endpoint returns items in a server-determined order that
  // may differ, so we re-sort the API response to match the previously
  // stored order. This keeps the "Already sent to kitchen" list stable.
  const existingTableData = getFromLocalStorage<TableData | undefined>(
    LocalStorageKey.TABLE_DATA,
    undefined
  );
  const existingItems = existingTableData?.order?.cart?.items ?? [];
  const responseItems = validData.order?.cart?.items ?? [];

  const sortedItems = preserveItemOrder(existingItems, responseItems);

  const dataToSave: TableData = {
    ...validData,
    order: {
      ...validData.order,
      cart: { ...validData.order.cart, items: sortedItems },
    },
  };

  saveToLocalStorage(LocalStorageKey.TABLE_DATA, dataToSave);
  if (scanToOrderInfo)
    saveToLocalStorage(LocalStorageKey.SCAN_TO_ORDER_INFO, scanToOrderInfo);
};

export const handleS2OTableData = (
  tableData: TableData | TableError | null,
  contactServerText: string,
  scanToOrderInfo?: ScanToOrderInfo,
  setTableData?: Dispatch<SetStateAction<TableData | TableError | null>>
) => {
  if (tableData) {
    if ("message" in tableData) {
      const invalidTableState = getInvalidTableState(contactServerText);
      saveToLocalStorage(LocalStorageKey.TABLE_DATA, invalidTableState);
      setTableData?.(invalidTableState);
      console.warn("Table is invalid or closed.");
    } else {
      handleS2OTableAPISuccess(tableData, scanToOrderInfo);
    }
  }
};
